import { MIN_OPEN_WIDTH } from "./sideNavWidth";

/** Rubber band toward the other width: the stretch nears this, never reaches it. */
export const RUBBER_BAND_PX = 70;
/** Stiffer rubber band past the widest or narrowest width. */
export const OVERDRAG_RUBBER_BAND_PX = 20;

export interface PointerSample {
    x: number;
    time: number;
}

export const rubberBand = ({
    distance,
    limit,
}: Record<"distance" | "limit", number>) =>
    (distance * limit) / (Math.abs(distance) + limit);

/**
 * The open nav's width with its edge at `pointerWidth`: the edge follows the
 * pointer between the minimum and `fittedWidth`, and rubber-bands past them
 * (softer below the minimum, where mini lies).
 */
export const openWidthAt = ({
    pointerWidth,
    fittedWidth,
}: Record<"pointerWidth" | "fittedWidth", number>) => {
    if (pointerWidth > fittedWidth) {
        return (
            fittedWidth +
            rubberBand({
                distance: pointerWidth - fittedWidth,
                limit: OVERDRAG_RUBBER_BAND_PX,
            })
        );
    }
    if (pointerWidth < MIN_OPEN_WIDTH) {
        return (
            MIN_OPEN_WIDTH +
            rubberBand({
                distance: pointerWidth - MIN_OPEN_WIDTH,
                limit: RUBBER_BAND_PX,
            })
        );
    }
    return pointerWidth;
};

/** Whole device pixels, so the nav's border never lands between two of them. */
export const toDevicePixels = (px: number) =>
    Math.round(px * window.devicePixelRatio) / window.devicePixelRatio;

/** Speed (px/ms) across the samples. */
export const velocityOf = (samples: PointerSample[]) => {
    const first = samples.at(0);
    const last = samples.at(-1);
    if (first === undefined || last === undefined || last.time === first.time) {
        return 0;
    }
    return (last.x - first.x) / (last.time - first.time);
};

const toMs = (duration: string) => {
    const value = Number.parseFloat(duration);
    if (!Number.isFinite(value)) {
        return 0;
    }
    return duration.trim().endsWith("ms") ? value : value * 1000;
};

/**
 * The nav's longest transition, resolved from its motion tokens (so a slowed
 * `--duration-slow` or reduced motion is respected). Read before a drag sets
 * `data-edge-drag`, which turns transitions off.
 */
export const longestTransitionMs = (nav: HTMLElement | null) =>
    nav === null
        ? 0
        : Math.max(
              0,
              ...getComputedStyle(nav).transitionDuration.split(",").map(toMs),
          );
